<template>
  <div class="ledger-page pb-5 pb-md-2">
    <div
      v-if="loading"
      class="text-center py-3"
    >
      <b-spinner variant="primary" />
    </div>
    <template v-else-if="item">
      <b-card class="rounded-lg">
        <div class="d-flex justify-content-between align-items-start flex-wrap">
          <div>
            <h3 class="mb-50">
              {{ item.code }}
            </h3>
            <div>{{ item.name }}</div>
            <b-badge
              class="mt-50"
              variant="light-primary"
            >
              {{ kindLabel(item.kind) }}
            </b-badge>
          </div>
          <div class="d-flex flex-wrap">
            <b-button
              v-if="canEdit"
              size="sm"
              variant="outline-primary"
              class="rounded-pill mr-50 mb-50"
              :to="{ name: 'ledger-items-edit', params: { id: item.id } }"
            >
              {{ $t('ledger_item_edit') }}
            </b-button>
            <b-button
              v-if="canDelete"
              size="sm"
              variant="outline-danger"
              class="rounded-pill mb-50"
              @click="confirmDelete"
            >
              {{ $t('ledger_assets_delete') }}
            </b-button>
          </div>
        </div>
        <div class="ledger-item-kpis mt-1">
          <div>
            <span class="text-muted">{{ $t('ledger_item_qty_on_hand') }}</span>
            <strong class="ledger-num d-block">{{ qty(item.qty_on_hand) }}</strong>
          </div>
          <div>
            <span class="text-muted">{{ $t('ledger_item_received_qty') }}</span>
            <strong class="ledger-num d-block">{{ qty(item.received_qty) }}</strong>
          </div>
          <div>
            <span class="text-muted">{{ $t('ledger_item_issued_qty') }}</span>
            <strong class="ledger-num d-block">{{ qty(item.issued_qty) }}</strong>
          </div>
          <div>
            <span class="text-muted">{{ $t('ledger_item_sale_price') }}</span>
            <strong class="ledger-num d-block">{{ money(item.sale_price) }}</strong>
          </div>
        </div>
      </b-card>

      <b-card class="rounded-lg">
        <b-row>
          <b-col
            v-for="field in detailFields"
            :key="field.label"
            :cols="field.cols || 6"
            :md="field.md || 3"
            class="mb-1"
          >
            <span class="text-muted d-block">{{ field.label }}</span>
            <span :class="{ 'ledger-num': field.numeric }">{{ field.value }}</span>
          </b-col>
        </b-row>
      </b-card>

      <b-card
        v-if="canEdit"
        class="rounded-lg ledger-dense-form"
      >
        <h5>{{ $t('ledger_item_receipt') }}</h5>
        <p class="text-muted small">
          {{ $t('ledger_item_receipt_hint') }}
        </p>
        <p
          v-if="!hasPermission('view warehouses')"
          class="text-danger small"
        >
          {{ $t('ledger_no_permission') }}
        </p>
        <b-row>
          <b-col
            cols="12"
            md="4"
          >
            <b-form-group :label="$t('ledger_inv_warehouse')">
              <v-select
                v-model="receipt.warehouse_id"
                :dir="isRtl ? 'rtl' : 'ltr'"
                :options="warehouseOptions"
                :reduce="option => option.id"
                label="label"
                :placeholder="$t('ledger_inv_warehouse')"
                class="ledger-account-select"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="6"
            md="3"
          >
            <b-form-group :label="$t('ledger_item_receipt_qty')">
              <b-form-input
                v-model.number="receipt.quantity"
                type="number"
                min="0.001"
                step="0.001"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="6"
            md="2"
          >
            <b-form-group :label="$t('ledger_item_receipt_price')">
              <b-form-input
                v-model.number="receipt.unit_price"
                type="number"
                min="0"
                step="0.01"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="3"
          >
            <b-form-group :label="$t('ledger_item_receipt_date')">
              <b-form-input
                v-model="receipt.movement_date"
                type="date"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-button
          variant="primary"
          class="rounded-pill"
          :disabled="acting || !receiptReady"
          @click="submitReceipt"
        >
          {{ $t('ledger_item_receipt') }}
        </b-button>
      </b-card>
    </template>
  </div>
</template>

<script>
import {
  BCard, BBadge, BButton, BRow, BCol, BFormGroup, BFormInput, BSpinner,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import Swal from 'sweetalert2'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { getItem, deleteItem, createItemReceipt } from '@/api/accounting/items'
import { getWarehouses } from '@/api/accounting/warehouses'
import {
  oneFrom, collectPages, formatLedgerMoney, formatLedgerQty, roundMoney, roundQty, apiErrorMessage,
} from '@/utils/ledger'

function kindLabel(kind) {
  if (kind && typeof kind === 'object') return kind.label_ar || kind.label || kind.value || ''
  return kind ? String(kind) : ''
}

function blankReceipt() {
  return {
    warehouse_id: null, quantity: null, unit_price: null, movement_date: '',
  }
}

export default {
  name: 'ItemShow',
  components: {
    BCard, BBadge, BButton, BRow, BCol, BFormGroup, BFormInput, BSpinner, vSelect,
  },
  data() {
    return {
      loading: false,
      acting: false,
      item: null,
      warehouses: [],
      receipt: blankReceipt(),
    }
  },
  computed: {
    isRtl() {
      return this.$store.state.appConfig.layout.isRTL
    },
    canEdit() {
      return this.hasPermission('edit items')
    },
    canDelete() {
      return this.hasPermission('delete items')
    },
    warehouseOptions() {
      return this.warehouses.map(warehouse => ({
        id: warehouse.id,
        label: [warehouse.code, warehouse.name].filter(Boolean).join(' — '),
      }))
    },
    receiptReady() {
      return Boolean(this.receipt.warehouse_id) && roundQty(this.receipt.quantity) > 0
    },
    detailFields() {
      const item = this.item || {}
      return [
        { label: this.$t('ledger_item_code'), value: this.shown(item.code) },
        { label: this.$t('ledger_item_name'), value: this.shown(item.name) },
        { label: this.$t('ledger_item_kind'), value: this.shown(kindLabel(item.kind)) },
        { label: this.$t('ledger_item_unit'), value: this.shown(item.unit) },
        { label: this.$t('ledger_item_group'), value: this.shown(item.group_name) },
        { label: this.$t('ledger_item_supplier'), value: this.shown(item.supplier_name) },
        { label: this.$t('ledger_item_finished_or_raw'), value: this.shown(item.finished_or_raw) },
        { label: this.$t('ledger_item_marked'), value: this.yesNo(item.marked) },
        { label: this.$t('ledger_item_in_use'), value: this.yesNo(item.in_use) },
        { label: this.$t('ledger_item_purchase_price'), value: this.money(item.purchase_price), numeric: true },
        { label: this.$t('ledger_item_sale_price'), value: this.money(item.sale_price), numeric: true },
        { label: this.$t('ledger_item_average_cost'), value: this.money(item.average_cost), numeric: true },
        { label: this.$t('ledger_item_last_price'), value: this.money(item.last_price), numeric: true },
        { label: this.$t('ledger_item_cost'), value: this.money(item.cost), numeric: true },
        { label: this.$t('ledger_item_opening_qty'), value: this.qty(item.opening_qty), numeric: true },
        { label: this.$t('ledger_item_requested_qty'), value: this.qty(item.requested_qty), numeric: true },
        { label: this.$t('ledger_item_description'), value: this.shown(item.description), cols: 12, md: 12 },
        { label: this.$t('ledger_item_entered_by'), value: this.personName(item.entered_by) },
        { label: this.$t('ledger_item_entered_at'), value: this.dateText(item.entered_at) },
        { label: this.$t('ledger_item_created_at'), value: this.dateText(item.created_at) },
      ]
    },
  },
  watch: {
    '$route.params.id': 'load',
  },
  mounted() {
    this.load()
    if (this.canEdit && this.hasPermission('view warehouses')) this.loadWarehouses()
  },
  methods: {
    kindLabel,
    shown(value) {
      if (value === null || value === undefined || value === '') return '—'
      return value
    },
    yesNo(value) {
      return value ? this.$t('ledger_yes') : this.$t('ledger_no')
    },
    money(value) {
      return formatLedgerMoney(value || 0, this.$i18n.locale)
    },
    qty(value) {
      return formatLedgerQty(value || 0, this.$i18n.locale)
    },
    personName(value) {
      if (!value) return '—'
      if (typeof value === 'string' || typeof value === 'number') return String(value)
      return value.name || value.name_ar || value.full_name || value.email || (value.id ? `#${value.id}` : '—')
    },
    dateText(value) {
      if (!value) return '—'
      return String(value).replace('T', ' ').replace(/\.\d+Z?$/, '').slice(0, 19)
    },
    toast(title, variant) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          icon: variant === 'success' ? 'CheckIcon' : 'AlertCircleIcon',
          variant,
        },
      })
    },
    applyUpdated(payload) {
      if (payload && payload.id && Object.prototype.hasOwnProperty.call(payload, 'qty_on_hand')) {
        this.item = payload
        return true
      }
      if (payload && payload.item && Object.prototype.hasOwnProperty.call(payload.item, 'qty_on_hand')) {
        this.item = payload.item
        return true
      }
      return false
    },
    async load() {
      this.loading = true
      try {
        this.item = oneFrom(await getItem(this.$route.params.id))
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), error?.response?.status === 422 ? 'warning' : 'danger')
      } finally {
        this.loading = false
      }
    },
    async loadWarehouses() {
      try {
        const first = await getWarehouses({ per_page: 200 })
        this.warehouses = await collectPages(page => getWarehouses({ per_page: 200, page }), first)
      } catch (error) {
        this.warehouses = []
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), error?.response?.status === 422 ? 'warning' : 'danger')
      }
    },
    async submitReceipt() {
      if (!this.receiptReady || this.acting) return
      const body = {
        warehouse_id: this.receipt.warehouse_id,
        quantity: roundQty(this.receipt.quantity),
      }
      if (this.receipt.unit_price !== null && this.receipt.unit_price !== '') {
        body.unit_price = roundMoney(this.receipt.unit_price)
      }
      if (this.receipt.movement_date) body.movement_date = this.receipt.movement_date
      this.acting = true
      try {
        const updated = oneFrom(await createItemReceipt(this.item.id, body))
        this.receipt = blankReceipt()
        if (!this.applyUpdated(updated)) {
          try {
            this.item = oneFrom(await getItem(this.item.id))
          } catch (refreshError) {
            this.toast(apiErrorMessage(refreshError, this.$t('ledger_error')), 'warning')
          }
        }
        this.toast(this.$t('ledger_item_receipt_saved'), 'success')
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), error?.response?.status === 422 ? 'warning' : 'danger')
      } finally {
        this.acting = false
      }
    },
    async confirmDelete() {
      const result = await Swal.fire({
        text: this.$t('ledger_item_delete_confirm'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('ledger_assets_delete'),
        cancelButtonText: this.$t('ledger_cancel'),
        customClass: { confirmButton: 'btn btn-danger', cancelButton: 'btn btn-outline-secondary ml-1' },
        buttonsStyling: false,
      })
      if (!result.isConfirmed) return
      try {
        await deleteItem(this.item.id)
        this.toast(this.$t('ledger_item_deleted'), 'success')
        this.$router.push({ name: 'ledger-items' })
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), error?.response?.status === 422 ? 'warning' : 'danger')
      }
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
.ledger-num { font-variant-numeric: tabular-nums; white-space: nowrap; }
.ledger-dense-form .card-body { padding: 0.75rem 1rem; }
.ledger-dense-form .form-group { margin-bottom: 0.35rem; }
.ledger-dense-form .form-group > label {
  font-size: 0.75rem;
  margin-bottom: 0.1rem;
}
.ledger-dense-form .form-control {
  height: 1.85rem;
  min-height: 1.85rem;
  padding: 0.15rem 0.45rem;
  font-size: 0.8125rem;
}
.ledger-dense-form .vs__dropdown-toggle {
  min-height: 1.85rem;
  padding-top: 0;
  padding-bottom: 0;
}
.ledger-dense-form .vs__selected,
.ledger-dense-form .vs__search {
  margin-top: 1px;
  margin-bottom: 1px;
  font-size: 0.8125rem;
}
.ledger-item-kpis { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0.75rem; }
@media (min-width: 768px) {
  .ledger-item-kpis { grid-template-columns: repeat(4, minmax(0, 1fr)); }
}
</style>
